import {
  faPlaneArrival,
  faPlaneDeparture,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { isSameDay } from 'date-fns';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Attribute } from '~/generated/graphql';
import {
  formatDate,
  formatDateInTimeZone,
  formatTime,
} from '~/helpers/formatDate';
import { ZoomLevel } from '~/routes/calendar._index';
import { Avatar } from '../ui/Avatar';

/**
 * ScheduleEventPill - Renders a job or booking pill in the calendar.
 * Memoized to prevent unnecessary re-renders when parent scrolls/updates.
 * Only re-renders when its own props change.
 */
export const ScheduleEventPill = memo(function ScheduleEventPill({
  event,
  height,
  width,
  zoomLevel,
  absolutePosition = true,
  showDates = false,
  selected = false,
  inDialog = false,
  filtered = false,
  edgeClip,
  subtext,
  showTitle = true,
  showScheduleInfo = true,
  showAvatar = true,
  showStatus = true,
}: {
  event: {
    summary: string;
    status: string;
    attributes?: Attribute[] | null;
    assignee?: {
      name: string;
      image?: string | null;
    } | null;
    scheduledStartTime?: string | null;
    scheduledEndTime?: string | null;
    scheduledStartStrict?: boolean | null;
    scheduledEndStrict?: boolean | null;
    start: string;
    end?: string;
    timeZone?: string | null;
  };
  height: React.CSSProperties['height'];
  width: React.CSSProperties['width'];
  zoomLevel?: ZoomLevel;
  absolutePosition?: boolean;
  showDates?: boolean;
  selected?: boolean;
  inDialog?: boolean;
  filtered?: boolean;
  /**
   * Controls corner rounding when a pill visually spans across week boundaries in monthly view.
   * When left is true, remove left rounding. When right is true, remove right rounding.
   */
  edgeClip?: { left?: boolean; right?: boolean };
  /** Optional one-line subtext (e.g., notes/tasks). Truncated if overflow. */
  subtext?: string;
  showTitle?: boolean;
  /** Whether to render the schedule info (times/dates) section */
  showScheduleInfo?: boolean;
  /** Whether to render the avatar (monthly mobile hides it) */
  showAvatar?: boolean;
  /** Whether to render the status label */
  showStatus?: boolean;
}) {
  const { t } = useTranslation(['translation', 'job']);

  const {
    status,
    attributes,
    summary,
    start: startDate,
    end: endDate,
    scheduledStartTime,
    scheduledEndTime,
    // scheduledStartStrict,
    // scheduledEndStrict,
    assignee,
    timeZone,
  } = event;

  // Extract guest name once for booking pills
  const bookingGuestName = attributes?.find((a) => a.name === 'Guest')
    ?.value?.[0];

  // Format date using timezone-aware formatting when timeZone is available
  const formatDateLocal = (datestr: string) => {
    if (timeZone) {
      return formatDateInTimeZone(datestr, timeZone, 'eee P');
    }
    // Use validated formatDate helper instead of direct Date constructor
    return formatDate(datestr, 'eee P');
  };

  const scheduleInfoBuilder: string[] = [];

  // Prefer explicit HH:mm from scheduledStartTime/scheduledEndTime.
  // Treat any explicit time (including "00:00" as midnight) as valid
  // Never derive from start/end when scheduledStartTime is null - start is a computed field
  // and may contain a time even when no explicit time was set.
  const derivedStartHHMM = scheduledStartTime || null;
  const derivedEndHHMM = scheduledEndTime || null;

  // Construct the schedule info string
  if (derivedStartHHMM) {
    scheduleInfoBuilder.push(formatTime(derivedStartHHMM));
    if (showDates) {
      scheduleInfoBuilder.push('on');
    }
  }
  if (showDates) {
    scheduleInfoBuilder.push(formatDateLocal(startDate));
  }
  if ((showDates && endDate) || (derivedStartHHMM && derivedEndHHMM)) {
    scheduleInfoBuilder.push('to');
  }
  if (derivedEndHHMM) {
    scheduleInfoBuilder.push(formatTime(derivedEndHHMM));
    if (showDates && endDate) {
      scheduleInfoBuilder.push('on');
    }
  }
  if (showDates && endDate) {
    scheduleInfoBuilder.push(formatDateLocal(endDate));
  }

  const scheduleInfo: React.ReactNode = scheduleInfoBuilder
    .filter((s) => s !== null)
    .join(' ');

  const isMultiDay =
    endDate && !isSameDay(new Date(startDate), new Date(endDate));

  return (
    <div
      className={classNames(
        // If the event has a colored attribute, use that, otherwise use the status color

        getAttributeColors(attributes) || getEventColors(status),
        'group flex touch-manipulation select-none flex-col items-stretch justify-between overflow-hidden text-xs leading-5 shadow-sm',
        status === 'Booking'
          ? height === 0
            ? 'hidden'
            : '-translate-y-1.5 scale-y-45 rounded-full px-2 transition-transform duration-300 hover:-translate-y-2.5 hover:scale-y-100 hover:shadow-md'
          : zoomLevel
          ? {
              'rounded-md transition-height': true, // Any non-booking event
              'pt-0.5': zoomLevel !== 'month', // Not smallest
              'px-[3px] pb-px pt-[2px]': zoomLevel === 'month', // Smallest size
              'px-[4px]': zoomLevel === 'default', // Middle size
              'px-[5px]': zoomLevel === 'week', // Biggest size
            }
          : inDialog
          ? 'rounded-md py-2 pl-3 pr-4 transition-height' // More spacing for dialog context
          : 'rounded-md px-2 transition-height', // Any non-booking event with no zoom level
        // Edge clipping to visually continue pills across week rows in monthly view
        edgeClip?.left && 'rounded-l-none',
        edgeClip?.right && 'rounded-r-none',
        selected && 'ring-1 ring-brand-lighter',
        filtered && 'opacity-50',
        // Enable container queries on all pills (used by booking pills)
        'cq',
        status === 'Booking' &&
          (bookingGuestName ? 'cq--has-guest' : 'cq--no-guest')
      )}
      style={{
        position: absolutePosition ? 'absolute' : 'unset',
        // transitionProperty: 'height, scale, translate',
        top: 0,
        left: 0,
        height: height,
        // the length of the event in days determines its width
        width: width,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        touchAction: 'manipulation',
      }}
    >
      <div>
        {status === 'Booking' ? (
          <div
            className={classNames(
              'flex w-full items-center justify-between opacity-0 transition-opacity duration-300 group-hover:opacity-100'
            )}
          >
            <>
              <div className='cq-left overflow-hidden whitespace-nowrap'>
                <FontAwesomeIcon icon={faPlaneArrival} />{' '}
                {scheduledStartTime ? formatTime(scheduledStartTime) : ''}
              </div>
              <p className='cq-center mx-2 min-w-0 flex-1 truncate text-center'>
                {bookingGuestName || summary || 'Booking'}
              </p>
              <p className='cq-right overflow-hidden whitespace-nowrap'>
                {scheduledEndTime ? formatTime(scheduledEndTime) : ''}
                <FontAwesomeIcon icon={faPlaneDeparture} />
              </p>
            </>
          </div>
        ) : (
          <>
            <div className='flex justify-between'>
              {showTitle && (
                <p
                  className={classNames(
                    'truncate font-medium',
                    assignee && zoomLevel !== 'month' && 'pr-3'
                  )}
                >
                  {summary}
                </p>
              )}
              {assignee && showAvatar && (
                <div className={classNames('h-5 w-5 flex-shrink-0')}>
                  <Avatar
                    name={assignee?.name}
                    image={assignee?.image}
                    size={'full-small-text'}
                    bg={'white'}
                  />
                </div>
              )}
            </div>
            {subtext && (
              <div className='mt-0.5 truncate text-[11px] text-gray-700/80'>
                {subtext}
              </div>
            )}
            <div className='flex justify-between pt-0.5'>
              {showScheduleInfo && <div>{scheduleInfo}</div>}
              {(() => {
                const shouldShowStatus =
                  showStatus && !(zoomLevel === 'month' && !isMultiDay);
                if (!shouldShowStatus) return null;
                return (
                  <div className='mt-auto w-fit whitespace-nowrap'>
                    {t('job:' + status)}
                  </div>
                );
              })()}
            </div>
          </>
        )}
      </div>
    </div>
  );
});

ScheduleEventPill.displayName = 'ScheduleEventPill';

function getAttributeColors(attributes?: Attribute[] | null) {
  const owner = attributes?.find((a) => a.name === 'Owner');
  if (owner?.value) {
    return 'bg-gray-500 text-white';
  }
}

function getEventColors(status: string) {
  switch (status) {
    case 'Booking':
      return 'bg-[#298888] text-white';
    case 'Active':
      return 'bg-status-active border-0.5 border-status-stroke-accepted text-white';
    case 'Inactive':
    case 'Incomplete':
      return 'bg-status-inactive text-white';
    case 'Complete':
      return 'bg-status-complete border-0.5 border-gray-700 text-white';
    case 'InProgress':
      return 'bg-status-inprogress border-0.5 border-status-stroke-inprogress text-white';
    case 'Accepted':
      return 'bg-status-accepted border-0.5 border-status-stroke-accepted text-white';
    case 'Cancelled':
      return 'bg-status-cancelled border-0.5 border-status-stroke-cancelled text-white';
    case 'Offered':
      return 'bg-status-offered border-0.5 border-status-stroke-offered text-white';
    default:
      return 'bg-surface-container-bright border-0.5 border-status-stroke-created text-gray-900';
  }
}
